import { HttpClient } from '@angular/common/http';
import { inject, Injectable, InjectionToken } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { catchError, concatMap, from, map, Observable } from 'rxjs';
import { StorageService } from 'src/app/shared/services/storage/storage.service';

export interface R1MenuItem {
  icon: string | undefined;
  label: string;
  sidebarLabel: string | undefined;
  url: string | undefined;
  items: R1MenuItem[];
}

type R1MenuItemsResponse = R1MenuItem[];

interface ServiceParameters {
  apiUrl: string;
  params?: {
    [param: string]:
      | string
      | number
      | boolean
      | ReadonlyArray<string | number | boolean>;
  };
}

export const ApiParametersToken = new InjectionToken<ServiceParameters>('');

@Injectable({ providedIn: 'root' })
export class MenuItemsApiService {
  private readonly storageService = inject(StorageService);

  // Dependencies
  private readonly httpClient = inject(HttpClient);
  private readonly apiParametersToken = inject(ApiParametersToken);

  // Menu items
  readonly menuItems = toSignal<R1MenuItemsResponse>(this.getMenuItems());
  private readonly menuItemsCacheKey = 'USER_MENU_CACHE';

  private getMenuItems(): Observable<R1MenuItemsResponse> {
    return this.httpClient
      .get<R1MenuItemsResponse>(
        this.apiParametersToken.apiUrl,
        !!this.apiParametersToken.params
          ? { params: this.apiParametersToken.params }
          : {}
      )
      .pipe(
        // if we fail to get the menu items, return the cached menu
        catchError(() => {
          return from(this.storageService.get(this.menuItemsCacheKey)).pipe(
            map((cached) => {
              if (!cached?.length) {
                return [];
              }
              return JSON.parse(cached);
            })
          );
        }),
        // save the returned menu items in cache for fallback if service is unavailable
        concatMap((res) => {
          return from(
            this.storageService.set(this.menuItemsCacheKey, JSON.stringify(res))
          ).pipe(map(() => res)); // return the original result and not the result of the set
        })
      );
  }
}
